import React from 'react';
import { useTranslation } from 'react-i18next';
import { Folder, MoreHorizontal, ChevronRight, ChevronDown } from 'lucide-react';
import { collectFolderSubtreeIds } from '../../utils/collectionFolders';
import InlineNameEditor from '../ui/InlineNameEditor';
import { SIDEBAR_TREE_ROW_ACTIVE, SIDEBAR_TREE_ROW_DROP, SIDEBAR_TREE_ROW_IDLE } from './sidebarState';

// ─── FolderRow ────────────────────────────────────────────────────────────────

export function folderRowPropsEqual(prev, next) {
  if (prev.folder !== next.folder) return false;
  if (prev.collection !== next.collection) return false;
  if (prev.onToggleFolder !== next.onToggleFolder) return false;
  if (prev.onSelectFolder !== next.onSelectFolder) return false;
  if (prev.onContextMenu !== next.onContextMenu) return false;
  if (prev.disableDnd !== next.disableDnd) return false;
  if (prev.activeDragId !== next.activeDragId) return false;
  if (prev.selectedFolder !== next.selectedFolder) return false;
  if (prev.editingItem !== next.editingItem) return false;
  if (prev.onSaveRename !== next.onSaveRename) return false;
  if (prev.onRenameDraft !== next.onRenameDraft) return false;
  if (prev.onCancelRename !== next.onCancelRename) return false;
  if (prev.dropIndicator !== next.dropIndicator) return false;
  if (prev.getHandleProps !== next.getHandleProps) return false;
  if (prev.fadeInIds !== next.fadeInIds) return false;
  // `isMarked` is a BOOLEAN, not the marked Set: the Set gets a fresh identity on
  // every ctrl-click, so passing it here would re-render the entire tree on every
  // gesture. Forgetting this line is silent — the row simply never repaints when
  // it is marked — which is what the aria-selected count test is there to catch.
  if (prev.isMarked !== next.isMarked) return false;
  if (prev.onRowClick !== next.onRowClick) return false;
  // `expandedFolders` is a single Set shared by the whole tree, so it gets a new
  // reference on every expand/collapse anywhere. Only re-render this row (and let
  // its `children` prop update) when a folder somewhere in *this* folder's own
  // subtree actually flipped — otherwise unrelated toggles would cascade globally.
  if (prev.expandedFolders !== next.expandedFolders) {
    const folders = prev.collection?.settings?.folders || [];
    const subtreeIds = collectFolderSubtreeIds(folders, prev.folder.id);
    for (const id of subtreeIds) {
      if (prev.expandedFolders.has(id) !== next.expandedFolders.has(id)) return false;
    }
  }
  // `children` (the nested CollectionTreeList element) is recreated by the parent
  // on every one of its re-renders regardless of whether anything relevant changed;
  // since all props that could make it meaningfully different are already checked
  // above, a differing reference alone doesn't warrant re-rendering this row.
  return true;
}

export const FolderRow = React.memo(function FolderRow({
  folder,
  collection,
  isMarked = false,
  onRowClick,
  expandedFolders,
  onToggleFolder,
  onSelectFolder,
  onContextMenu,
  disableDnd,
  activeDragId,
  selectedFolder,
  editingItem,
  onSaveRename,
  onRenameDraft,
  onCancelRename,
  dropIndicator,
  getHandleProps,
  fadeInIds,
  children,
}) {
  const { t } = useTranslation();
  const dndDisabled = disableDnd || collection.can_edit === false;
  const expanded  = expandedFolders.has(folder.id);
  const isEditing = editingItem?.type === 'folder' && String(editingItem.id) === String(folder.id);
  const isDragging = activeDragId === `folder-${folder.id}`;

  const ind = dropIndicator?.overId === `folder-${folder.id}` ? dropIndicator : null;
  const dropBefore = ind?.position === 'before';
  const dropAfter  = ind?.position === 'after';
  const isDropZone = ind?.position === 'inside';
  const isSelected =
    selectedFolder &&
    String(selectedFolder.id) === String(folder.id) &&
    String(selectedFolder.collectionId) === String(collection.id);

  return (
    <div
      className={`sidebar-row-wrap rounded-md${
        fadeInIds?.has(String(folder.id)) ? ' animate-fadeIn' : ''
      }`}
    >
      {dropBefore && <div className="sidebar-drop-line" aria-hidden />}
      <div
        data-drag-id={`folder-${folder.id}`}
        style={{ paddingLeft: '4px', opacity: isDragging ? 0.4 : undefined }}
        className={`flex items-center px-2 py-1 rounded-md group cursor-pointer transition-colors duration-150 ${
          isDropZone
            ? SIDEBAR_TREE_ROW_DROP
            : isSelected || isMarked
              ? SIDEBAR_TREE_ROW_ACTIVE
              : SIDEBAR_TREE_ROW_IDLE
        }`}
        role="treeitem"
        aria-selected={isMarked}
        onClick={(e) => {
          // A modifier-click is a MARKING gesture and must never reach the open
          // path — see useTreeMultiSelect for why that matters.
          if (onRowClick?.(e, `folder-${folder.id}`)) return;
          onSelectFolder?.(folder, collection.id);
        }}
        onContextMenu={(e) => onContextMenu(e, 'folder', { ...folder, collectionId: collection.id })}
        {...(!dndDisabled && !isEditing ? getHandleProps(`folder-${folder.id}`) : {})}
      >
        <button
          type="button"
          className="sidebar-collection-chevron-btn mr-1 shrink-0 flex items-center p-0.5 rounded"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onToggleFolder(folder.id); }}
          aria-label={expanded ? t('sidebar.collapseFolder') : t('sidebar.expandFolder')}
        >
          {expanded ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
        </button>
        <Folder size={12} className="text-amber-600 shrink-0 mr-1" aria-hidden />
        {isEditing ? (
          <InlineNameEditor
            value={folder.name}
            onSave={(name) => onSaveRename?.('folder', folder.id, name)}
            onDraftChange={(draft) => onRenameDraft?.('folder', folder.id, draft)}
            onCancel={() => onCancelRename?.()}
          />
        ) : (
          <span className="flex-1 min-w-0 text-xs font-medium text-gray-800 dark:text-zinc-200 truncate">
            {folder.name}
          </span>
        )}
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onContextMenu(e, 'folder', { ...folder, collectionId: collection.id }); }}
          className="opacity-0 group-hover:opacity-100 shrink-0 p-1 bg-transparent hover:bg-gray-200 dark:hover:bg-zinc-700 rounded transition-colors duration-150 cursor-pointer"
        >
          <MoreHorizontal size={12} className="text-gray-500 dark:text-zinc-400" />
        </button>
      </div>
      {expanded && (
        <div className="sidebar-folder-scope">
          {children}
        </div>
      )}
      {dropAfter && <div className="sidebar-drop-line" aria-hidden />}
    </div>
  );
}, folderRowPropsEqual);
